/**
 * The shared selectors are general selectors that other selector files may require.
 * They are not put directly into index.js to prevent circular references.
 */
import { productOpen } from 'event-widgets/utils/product';
import { find, flatMap } from 'lodash';
import { sessionStatus } from 'event-widgets/redux/selectors/event';
import { getNonGuestRegistrationsForDisplay } from 'event-widgets/utils/eventRegistration';
import { getEventRegistrations } from '../registrationForm/regCart/selectors';
import { createSelector } from 'reselect';
import { getIn } from 'icepick';
import { getRegistrationPackId } from '../userSession';

const EMPTY_ARRAY = Object.freeze([]);

export const getRegistrationForm = (state) => state.registrationForm;

export function getRegCart(state) {
  const registrationForm = getRegistrationForm(state);
  return registrationForm && registrationForm.regCart;
}

export const getRegistrationPaths = (state) => state.appData.registrationSettings.registrationPaths;

/**
 * Determines if the admission item is visible for the given registration type. A visible
 * admission item can be selected by a registrant.
 *
 * @param {*} options.registeredAdmissionItem During registration modification
 *  a prior selected admission item will be visible even if it would normally not be shown.
 * @param {*} options.includeClosedAdmissionItems Determines if closed admission items should
 *  be shown. Used during planner registration to allow a planner to select closed admission item.
 */
export function admissionItemIsVisible(
  registrationTypeId,
  admissionItem,
  options
) {
  const { registeredAdmissionItem, includeClosedAdmissionItems = false } = options || {};
  const registeredAdmissionItemId = registeredAdmissionItem && registeredAdmissionItem.productId;
  const selectedDuringInitialRegistration = admissionItem.id === registeredAdmissionItemId;
  const visibleForRegistration = includeClosedAdmissionItems || productOpen(admissionItem);
  const visibleForRegistrationType =
    admissionItem.applicableContactTypes.length === 0 ||
    admissionItem.applicableContactTypes.includes(registrationTypeId);

  return selectedDuringInitialRegistration || (visibleForRegistration && visibleForRegistrationType);
}

/**
 * Determines if the session is visible for the given registration type.
 */
export function sessionIsVisibleForRegistrationType(registrationTypeId, session) {
  const associatedRegistrationTypes = session.associatedRegistrationTypes || [];
  return associatedRegistrationTypes.length === 0
    || associatedRegistrationTypes.includes(registrationTypeId);
}

/**
 * Determines if the session is visible for the given admission item. If no admission
 * item is present the session is considered visible.
 */
export function sessionIsVisibleForAdmissionItem(admissionItem, session) {
  return !admissionItem
    || !admissionItem.limitOptionalSessionsToSelect
    || admissionItem.availableOptionalSessions.includes(session.id);
}

/**
 * Determines if the session is visible for the given registration type and admission item. A
 * visible session can be selected by the registrant.
 *
 * @param {*} options.registeredSessions During registration modification amy prior sessions explicitly
 *  selected by the registrant will be visible even if it would normally not be shown.
 * @param {*} options.includeClosedSessions Determines if closed sessions should
 *  be shown. Used during planner registration to allow a planner to select closed sessions.
 */
export function sessionIsVisible(
  registrationTypeId,
  admissionItem,
  session,
  options
) {
  const { registeredSessions = {}, includeClosedSessions = false,
    regPathAllowsSessionSelection = true } = options || {};
  const registeredSession = registeredSessions[session.id];
  const selectedDuringInitialRegistration =
    registeredSession && registeredSession.registrationSourceType === 'Selected';
  const visibleForRegistration = !session.isIncludedSession && (includeClosedSessions || productOpen(session))
    && session.status !== sessionStatus.cancelled;

  return (
    selectedDuringInitialRegistration ||
    (!session.isIncludedSession &&
      visibleForRegistration &&
      regPathAllowsSessionSelection &&
      sessionIsVisibleForRegistrationType(registrationTypeId, session) &&
      sessionIsVisibleForAdmissionItem(admissionItem, session))
  );
}

export function sessionGroupIsVisible(
  sessionGroup,
  options
) {
  let {
    includeClosedSessionGroups = false,
    registeredSessions = {},
    sessions = {}
  } = options || {};
  const containsRegisteredSession = find(sessions, session => {
    const registeredSession = registeredSessions && registeredSessions[session.id];
    return registeredSession && registeredSession.registrationSourceType === 'Selected';
  });
  const visibleForRegistration = includeClosedSessionGroups || (productOpen(sessionGroup) || containsRegisteredSession);
  return visibleForRegistration;
}

/**
 * Determines if the session is associated to the admission item. An associated session is automatically
 * added to the registrants cart without fees.
 */
export function sessionIsAssociatedToAdmissionItem(admissionItem, session) {
  const sessionsAssociatedWithAdmissionItem = admissionItem ? admissionItem.associatedOptionalSessions : [];
  return sessionsAssociatedWithAdmissionItem.includes(session.id);
}

/**
 * Determines if the session is registerable for the given registration type and admission item. A registerable
 * session is one that a registrant can select, is included with the event, or associated to an admission item.
 *
 * @param {*} options.registeredSessions See sessionIsVisible()
 * @param {*} options.includeClosedSessions See sessionIsVisible()
 */
export function sessionIsRegisterable(registrationTypeId,
  admissionItem,
  session,
  options
) {
  return session.isIncludedSession
    || sessionIsAssociatedToAdmissionItem(admissionItem, session)
    || sessionIsVisible(registrationTypeId, admissionItem, session, options);
}

/**
 * Get id of the reg path associated with a given reg type
 */
export function getAssociatedRegistrationPathId(state, registrationTypeId) {
  const registrationPaths = state.appData.registrationSettings.registrationPaths;
  const associatedRegPath = find(registrationPaths, rp => rp.associatedRegistrationTypes.includes(registrationTypeId));
  if (associatedRegPath) {
    return associatedRegPath.id;
  }
  const defaultRegPath = find(registrationPaths, rp => rp.isDefault);
  return defaultRegPath.id;
}

/**
 * Check whether a given reg path allows session selection
 */
export function allowsSessionSelection(state, regPathId) {
  return state.appData.registrationSettings.registrationPaths[regPathId].allowsSessionSelection;
}

/**
 * Check whether a given reg path allows guest registration
 */
export function allowsGuestRegistration(state, regPathId) {
  return state.appData.registrationSettings.registrationPaths[regPathId].guestRegistrationSettings &&
    state.appData.registrationSettings.registrationPaths[regPathId]
      .guestRegistrationSettings.isGuestRegistrationEnabled;
}

/**
 *
 * @param {*} state
 */
export const getCountries = (state) => state.countries && state.countries.countries;

export const getCountriesAsChoices = createSelector(getCountries,
  (countries) => Object.keys(countries || {}).map((code) => ({
    text: countries[code].nameResourceKey,
    id: code
  }))
);

/**
 * Check whether a given reg path allows admin registration
 */
export function isAdministratorRegistrationEnabled(state, regPathId) {
  return !!getIn(state, ['appData', 'registrationSettings', 'registrationPaths', regPathId,
    'identityConfirmation', 'administratorRegistrationEnabled']);
}

/**
 * Check whether a given reg path allows group registration
 */
export function isGroupRegistrationEnabled(state, regPathId) {
  return !!getIn(state, ['appData', 'registrationSettings', 'registrationPaths', regPathId,
    'allowsGroupRegistration']);
}

/**
 * Check whether a given reg path has vouchers enabled
 */
export function isVoucherEnabled(state, regPathId) {
  return !!getIn(state, ['appData', 'registrationSettings', 'registrationPaths', regPathId,
    'voucherEnabled']);
}

/**
 * Get the registration types that are associated with a private registration path
 */
export const getEmailOnlyRegistrationTypes = createSelector(
  state => getRegistrationPaths(state),
  (registrationPaths) => {
    return flatMap(Object.values(registrationPaths)
      .filter(regPath => regPath.accessRules.invitationListAccess.isEmailOnlyInvite)
      .map(regPath => regPath.associatedRegistrationTypes));
  });

/**
 * returns all invitees (non guest) event registration ids
 */
export const getAllInviteesRegistrationIds = createSelector(
  // Selector arguments
  getRegCart,
  // result function
  (regCart) => {
    const eventRegistrations = getEventRegistrations(regCart);
    return getNonGuestRegistrationsForDisplay(Object.values(eventRegistrations))
      .map(eventRegistration => eventRegistration.eventRegistrationId);
  }
);

/**
 * Returns all the active account hotel billing instructions present in the state
 */
export const getActiveAccountHotelBillingInstructions = (state) => {
  return ((getIn(state, ['account', 'hotelBillingInstructions']) || EMPTY_ARRAY)
    .filter(bi => bi.isActive) || EMPTY_ARRAY)
    .sort((one, two) => one.name.localeCompare(two.name));
};

/**
  * Returns the registration pack Id from the regCart
*/
const getRegPackIdFromRegCart = (state) => {
  const regCart = getRegCart(state);
  if (regCart && !!regCart.regPackId) {
    return regCart.regPackId;
  }
  return null;
};

/**
  * Returns the registration pack Id
*/
export const getRegPackId = (state) => {
  /* During red mode the regpack Id will be present in the regCart */
  let registrationPackId = getRegPackIdFromRegCart(state);
  if (!registrationPackId) {
    registrationPackId = getRegistrationPackId(state);
  }
  return registrationPackId;
};
